import { Skeleton } from './ui/skeleton.js';

/**
 * Reusable shimmer placeholders that mirror the shape of common page
 * primitives (card grids, list rows, group sections). Beats the
 * raw "Loading…" text we had previously — the operator sees the
 * layout settle and where data will land before it arrives.
 *
 * All skeletons render against the dark theme by passing the shared
 * `bg-foreground/10` tint to the underlying shadcn `Skeleton` primitive.
 */

export function CardGridSkeleton({
  rows = 3,
  columns = 3,
}: {
  rows?: number;
  columns?: number;
}): JSX.Element {
  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {Array.from({ length: rows * columns }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-md border border-foreground/10 bg-foreground/[0.03] p-4"
        >
          <Skeleton className="h-10 w-10 shrink-0 rounded-md bg-foreground/10" />
          <div className="grow space-y-2">
            <Skeleton className="h-4 w-32 bg-foreground/10" />
            <Skeleton className="h-3 w-20 bg-foreground/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ListSkeleton({
  rows = 5,
  withChip = false,
}: {
  rows?: number;
  /** Render a leading round chip placeholder per row (icon position). */
  withChip?: boolean;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-md border border-foreground/10 bg-foreground/[0.02] p-3"
        >
          {withChip ? (
            <Skeleton className="h-8 w-8 shrink-0 rounded-full bg-foreground/10" />
          ) : null}
          <div className="grow space-y-1.5">
            <Skeleton className="h-3.5 w-1/3 bg-foreground/10" />
            <Skeleton className="h-3 w-1/4 bg-foreground/10" />
          </div>
          <Skeleton className="h-6 w-16 bg-foreground/10" />
        </div>
      ))}
    </div>
  );
}

export function GroupedListSkeleton({
  groups = 2,
  itemsPerGroup = 3,
}: {
  groups?: number;
  itemsPerGroup?: number;
}): JSX.Element {
  return (
    <div className="space-y-4">
      {Array.from({ length: groups }).map((_, g) => (
        <section key={g} className="rounded-md border border-foreground/10 bg-foreground/[0.02]">
          <header className="flex items-center gap-3 border-b border-foreground/10 px-4 py-2">
            <Skeleton className="h-5 w-5 rounded-full bg-foreground/10" />
            <Skeleton className="h-3.5 w-32 bg-foreground/10" />
          </header>
          <div className="divide-y divide-white/5">
            {Array.from({ length: itemsPerGroup }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                <Skeleton className="h-8 w-8 shrink-0 rounded-full bg-foreground/10" />
                <div className="grow space-y-1.5">
                  <Skeleton className="h-3.5 w-1/3 bg-foreground/10" />
                  <Skeleton className="h-3 w-1/5 bg-foreground/10" />
                </div>
                <Skeleton className="h-5 w-20 bg-foreground/10" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
